import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, ShoppingBag, BadgeCheck, PackageCheck, Upload, Truck, ChevronRight } from "lucide-react";
import { OrderAPI, MerchantAPI, PaymentAPI } from "@/lib/api";

// ============================================================================
// Floating button notifikasi Ruang Anggota.
// - Muncul di kanan-bawah semua halaman /anggota.
// - Bouncing + badge berdenyut bila ada informasi baru (pesanan masuk, bukti
//   pembayaran perlu diverifikasi, barang tiba, dsb).
// - Klik tombol  -> pop-up daftar informasi terbaru.
// - Klik item    -> diarahkan ke halaman tujuan (mis. Jualan Saya tab Pesanan Masuk).
// ============================================================================

const POLL_MS = 30000; // segarkan tiap 30 detik

export default function MemberNotifications() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sellerConfirmations, setSellerConfirmations] = useState([]);
  const [sellerOrders, setSellerOrders] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [myConfirmations, setMyConfirmations] = useState([]);
  const timerRef = useRef(null);

  const load = async () => {
    // Sisi penjual (abaikan 403 bila anggota belum punya toko).
    MerchantAPI.paymentConfirmations("PENDING").then(setSellerConfirmations).catch(() => setSellerConfirmations([]));
    MerchantAPI.orders().then(setSellerOrders).catch(() => setSellerOrders([]));
    // Sisi pembeli.
    OrderAPI.mine().then(setMyOrders).catch(() => setMyOrders([]));
    PaymentAPI.mine().then(setMyConfirmations).catch(() => setMyConfirmations([]));
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  // Susun daftar informasi terbaru dari data yang ada.
  const items = useMemo(() => {
    const out = [];

    // PENJUAL: bukti transfer pembeli menunggu verifikasi Anda.
    if (sellerConfirmations.length > 0) {
      out.push({
        key: "verify",
        icon: BadgeCheck,
        tone: "bg-amber-warm/20 text-amber-700",
        title: `${sellerConfirmations.length} bukti pembayaran perlu diverifikasi`,
        desc: "Pembeli menunggu Anda memeriksa bukti transfernya.",
        to: "/anggota/jualan?tab=pesanan",
      });
    }

    // PENJUAL: pesanan dibayar, perlu diterima/dikemas.
    const needAccept = sellerOrders.filter((o) => o.status === "PAID").length;
    if (needAccept > 0) {
      out.push({
        key: "accept",
        icon: ShoppingBag,
        tone: "bg-terracotta/15 text-terracotta",
        title: `${needAccept} pesanan masuk perlu diproses`,
        desc: "Terima pesanan lalu kemas untuk dikirim.",
        to: "/anggota/jualan?tab=pesanan",
      });
    }

    // PENJUAL: pesanan dikemas, siap dikirim.
    const needShip = sellerOrders.filter((o) => o.status === "PACKED").length;
    if (needShip > 0) {
      out.push({
        key: "ship",
        icon: Truck,
        tone: "bg-sage/25 text-forest",
        title: `${needShip} pesanan siap dikirim`,
        desc: "Kirim pesanan yang sudah dikemas.",
        to: "/anggota/jualan?tab=pesanan",
      });
    }

    // PEMBELI: barang sudah dikirim — konfirmasi terima.
    const arrived = myOrders.filter((o) => o.status === "SHIPPED").length;
    if (arrived > 0) {
      out.push({
        key: "arrived",
        icon: PackageCheck,
        tone: "bg-forest/10 text-forest",
        title: `${arrived} pesanan sedang dikirim ke Anda`,
        desc: "Konfirmasi terima barang setelah pesanan tiba.",
        to: "/anggota/pesanan",
      });
    }

    // PEMBELI: pesanan belum dibayar / bukti ditolak.
    const confirmationFor = (orderId) => myConfirmations.find((c) => c.order_id === orderId && c.purpose === "ORDER");
    const needPay = myOrders.filter((o) => o.status === "PENDING" && confirmationFor(o.id)?.status !== "PENDING");
    if (needPay.length > 0) {
      const rejected = needPay.some((o) => confirmationFor(o.id)?.status === "REJECTED");
      out.push({
        key: "pay",
        icon: Upload,
        tone: "bg-stone-soft/50 text-ink/70",
        title: rejected ? "Bukti pembayaran ditolak — kirim ulang" : `${needPay.length} pesanan menunggu bukti pembayaran`,
        desc: "Unggah bukti transfer agar pesanan diproses penjual.",
        to: "/anggota/pesanan",
      });
    }

    return out;
  }, [sellerConfirmations, sellerOrders, myOrders, myConfirmations]);

  const total = items.length;

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <>
      {/* Pop-up daftar informasi */}
      {open && (
        <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)}>
          <div
            className="absolute above-tabbar-2 right-5 lg:right-8 w-[calc(100vw-2.5rem)] max-w-sm bg-white rounded-2xl border border-stone-soft shadow-2xl overflow-hidden animate-scale-in"
            onClick={(e) => e.stopPropagation()}
            data-testid="member-notif-popup"
          >
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-stone-soft bg-cream/60">
              <div className="flex items-center gap-2 font-display font-semibold text-ink text-sm">
                <Bell size={15} className="text-forest" /> Informasi Terbaru
              </div>
              <button onClick={() => setOpen(false)} className="text-ink/55 hover:text-ink"><X size={16} /></button>
            </div>
            {total === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-ink/55">Tidak ada informasi baru. 🎉</div>
            ) : (
              <ul className="max-h-96 overflow-y-auto divide-y divide-stone-soft">
                {items.map((it) => (
                  <li key={it.key}>
                    <button
                      onClick={() => go(it.to)}
                      className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-cream/70 transition text-left"
                      data-testid={`member-notif-item-${it.key}`}
                    >
                      <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${it.tone}`}>
                        <it.icon size={17} />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-ink leading-snug">{it.title}</span>
                        <span className="block text-xs text-ink/55 mt-0.5">{it.desc}</span>
                      </span>
                      <ChevronRight size={15} className="text-ink/40 shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`fixed above-tabbar right-5 lg:right-8 z-[59] w-14 h-14 rounded-full grid place-items-center shadow-xl transition
          ${total > 0 ? "bg-forest text-white animate-bounce hover:animate-none" : "bg-white text-ink/60 border border-stone-soft hover:bg-cream"}`}
        aria-label="Informasi terbaru"
        data-testid="member-notif-fab"
      >
        <Bell size={22} />
        {total > 0 && (
          <>
            {/* Badge jumlah + efek denyut */}
            <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-terracotta text-white text-[11px] font-bold grid place-items-center border-2 border-white">
              {total > 9 ? "9+" : total}
            </span>
            <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-terracotta animate-ping opacity-60 pointer-events-none" />
          </>
        )}
      </button>
    </>
  );
}
